import { AnimatePresence, motion } from 'framer-motion';

import { useEffect, useState } from 'react';

import { Box, Center, Loader, Stack, Text } from '@optiro/ui';

interface InteractiveLoaderProps {
  /** Main loading message */
  message?: string;
  /** Array of rotating messages that cycle through */
  rotatingMessages?: string[];
  /** Custom loader size */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Animation speed for message rotation (in milliseconds) */
  rotationSpeed?: number;
}

const defaultRotatingMessages = [
  'Indlæser...',
  'Et øjeblik...',
  'Næsten der...',
  'Behandler din anmodning...',
];

export function InteractiveLoader({
  message = 'Indlæser',
  rotatingMessages = defaultRotatingMessages,
  size = 'lg',
  rotationSpeed = 2000,
}: InteractiveLoaderProps) {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  useEffect(() => {
    if (rotatingMessages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % rotatingMessages.length);
    }, rotationSpeed);

    return () => clearInterval(interval);
  }, [rotatingMessages.length, rotationSpeed]);

  return (
    <Box
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        zIndex: 9999,
      }}
    >
      <Center h="100vh">
        <Stack align="center" gap="xl">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <Loader size={size} type="dots" />
          </motion.div>

          <Stack align="center" gap="sm">
            <Text size="lg" fw={600} c="gray.7">
              {message}
            </Text>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentMessageIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <Text size="sm" c="gray.5" ta="center">
                  {rotatingMessages[currentMessageIndex]}
                </Text>
              </motion.div>
            </AnimatePresence>
          </Stack>
        </Stack>
      </Center>
    </Box>
  );
}
